import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { customerService } from '../../services/customerService';
import type { CustomerActivity, CreateActivityDto, UpdateActivityDto } from '../../types/customer360';
import { X } from 'lucide-react';

interface ActivityModalProps {
  customerId: number;
  activity: CustomerActivity | null;
  onClose: () => void;
  onSave: () => void;
}

const activityTypes = [
  { value: 'note', label: 'Note' },
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'task', label: 'Task' },
  { value: 'ticket', label: 'Ticket' },
  { value: 'other', label: 'Other' }
] as const;

const directions = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' }
] as const;

const OUTCOME_MAX_LENGTH = 100;

export default function ActivityModal({ customerId, activity, onClose, onSave }: ActivityModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    activity_type: activity?.activity_type || 'note' as const,
    subject: activity?.subject || '',
    description: activity?.description || '',
    activity_date: activity?.activity_date
      ? new Date(activity.activity_date).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
    duration_minutes: activity?.duration_minutes?.toString() || '',
    direction: activity?.direction || '',
    outcome: activity?.outcome || '',
    related_ticket_id: activity?.related_ticket_id?.toString() || ''
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildErrorMessage = (error: unknown) => {
    const responseMessage =
      (typeof error === 'object' && error !== null && 'response' in error && (error as any).response?.data?.message)
        ? (error as any).response.data.message
        : undefined;
    const fallbackMessage = error instanceof Error ? error.message : undefined;
    const normalizedMessage = (responseMessage || fallbackMessage || '').toString();

    if (/value too long/i.test(normalizedMessage) || /character varying\(100\)/i.test(normalizedMessage)) {
      return `Outcome can include at most ${OUTCOME_MAX_LENGTH} characters. Please shorten the text and try again.`;
    }

    return `Failed to ${activity ? 'update' : 'create'} activity`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (activity) {
        // Update existing activity - activity_type cannot be changed
        const updateData: UpdateActivityDto = {
          subject: formData.subject || undefined,
          description: formData.description || undefined,
          activity_date: formData.activity_date ? new Date(formData.activity_date).toISOString() : undefined,
          duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : undefined,
          direction: formData.direction ? (formData.direction as 'inbound' | 'outbound') : undefined,
          outcome: formData.outcome || undefined,
          related_ticket_id: formData.related_ticket_id ? parseInt(formData.related_ticket_id) : undefined
        };
        await customerService.updateActivity(activity.id, updateData);
        showToast('Activity updated successfully', 'success');
      } else {
        // Create new activity
        const createData: Omit<CreateActivityDto, 'customer_id'> = {
          activity_type: formData.activity_type,
          subject: formData.subject || undefined,
          description: formData.description || undefined,
          activity_date: formData.activity_date ? new Date(formData.activity_date).toISOString() : undefined,
          duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : undefined,
          direction: formData.direction ? (formData.direction as 'inbound' | 'outbound') : undefined,
          outcome: formData.outcome || undefined,
          related_ticket_id: formData.related_ticket_id ? parseInt(formData.related_ticket_id) : undefined,
          created_by: user?.id
        };
        await customerService.createActivity(customerId, createData);
        showToast('Activity created successfully', 'success');
      }

      onSave();
    } catch (error) {
      console.error('Error saving activity:', error);
      showToast(buildErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const outcomeCharsUsed = formData.outcome.length;
  const outcomeCharsRemaining = Math.max(OUTCOME_MAX_LENGTH - outcomeCharsUsed, 0);

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
      <form
        onSubmit={handleSubmit}
        className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] ${
          isDark ? 'bg-gray-800' : 'bg-white'
        } flex flex-col`}
      >
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {activity ? 'Edit Activity' : 'Create New Activity'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={`p-1 rounded-lg transition-colors ${
              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
          >
            <X className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Activity Type */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Activity Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.activity_type}
              onChange={(e) => handleChange('activity_type', e.target.value)}
              required
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {activityTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Subject
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="Brief summary of the activity"
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Description */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Detailed notes about this activity"
              rows={5}
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Activity Date and Time */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Activity Date & Time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={formData.activity_date}
              onChange={(e) => handleChange('activity_date', e.target.value)}
              required
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Duration */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                Duration (minutes)
              </label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => handleChange('duration_minutes', e.target.value)}
                placeholder="15"
                min="1"
                className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>

            {/* Direction */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                Direction
              </label>
              <select
                value={formData.direction}
                onChange={(e) => handleChange('direction', e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">Select direction</option>
                {directions.map((dir) => (
                  <option key={dir.value} value={dir.value}>
                    {dir.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Outcome */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                Outcome
              </label>
              <span className={`text-xs ${outcomeCharsRemaining === 0 ? 'text-red-500' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {outcomeCharsRemaining} / {OUTCOME_MAX_LENGTH} characters remaining
              </span>
            </div>
            <input
              type="text"
              value={formData.outcome}
              onChange={(e) => handleChange('outcome', e.target.value)}
              placeholder="e.g., Resolved issue, Scheduled follow-up"
              maxLength={OUTCOME_MAX_LENGTH}
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Maximum of {OUTCOME_MAX_LENGTH} characters.
            </p>
          </div>

          {/* Related Ticket ID */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Related Ticket ID
            </label>
            <input
              type="number"
              value={formData.related_ticket_id}
              onChange={(e) => handleChange('related_ticket_id', e.target.value)}
              placeholder="Link to a ticket (optional)"
              min="1"
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

        </div>

        <div className={`flex items-center justify-end gap-3 border-t px-6 py-4 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={`px-6 py-2 rounded-lg border transition-colors ${
              isDark
                ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : activity ? 'Update Activity' : 'Create Activity'}
          </button>
        </div>
      </form>
    </div>
  );

  return createPortal(modalContent, document.body);
}
